import * as React from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { LineChart, BarChart } from '@mui/x-charts';
import { Gauge, gaugeClasses } from '@mui/x-charts/Gauge';
import { Dayjs } from 'dayjs';
import {
  getElectricityCost,
  getPulseConsumption,
  CostDataPoint,
  ConsumptionDataPoint,
  getGasCost,
  getWaterCost,
  getVibration,
  VibrationDataPoint,
  getImportCost,
  getImportConsumption,
  getAirQuality,
  AirQualityDataPoint,
  getThreePhaseCurrent,
  ThreePhaseDataPoint
} from '../../services/api.ts';
import BaseChartVisualization from './basechart.tsx';
import {
  ChartType,
  SensorGaugeConfig,
  PulseConsumptionConfig,
  WidgetComponentProps,
} from './widgetRegistry.ts';
import { DESC_MAP } from '../../utils/utils.ts';


type SeriesPoint = { bucket: string; value: number };

function renderChart(
  chartType: ChartType,
  data: SeriesPoint[],
  label: string,
  unit: string,
  colour: string,
  min?: number,
  max?: number,
) {
  if (!data.length) return <Typography>No data found</Typography>;

  const xLabels = data.map((d) => new Date(d.bucket).toLocaleString());
  const values  = data.map((d) => d.value);
  const total   = values.reduce((s, v) => s + v, 0);
  const avg     = total / values.length;

  if (chartType === 'stat') {
    return (
      <Box sx={{ textAlign: 'center', py: 2 }}>
        <Typography variant="h3" fontWeight="bold" color={colour}>
          {total.toFixed(2)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {label} (total {unit})
        </Typography>
      </Box>
    );
  }

  if (chartType === 'gauge') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Gauge
          value={avg}
          valueMin={min ?? 0}
          valueMax={max ?? Math.max(...values) * 1.2}
          cornerRadius="50%"
          sx={{
            [`& .${gaugeClasses.valueText}`]: { fontSize: 20, fontWeight: 'bold' },
            [`& .${gaugeClasses.valueArc}`]:  { fill: colour },
          }}
        />
        <Typography variant="body2" color="text.secondary">{label}</Typography>
      </Box>
    );
  }

  const series = [{ data: values, label, color: colour, showMark: false,
    valueFormatter: (v: number | null) => v !== null ? `${v.toFixed(2)} ${unit}` : '' }];

  if (chartType === 'bar') {
    return (
      <Box sx={{ width: '100%', height: 300 }}>
        <BarChart
          series={series}
          xAxis={[{ data: xLabels, label: 'Time' }]}
          yAxis={[{ width: 60, label: unit }]}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height: 300 }}>
      <LineChart
        series={series}
        xAxis={[{ scaleType: 'point', data: xLabels, label: 'Time' }]}
        yAxis={[{ width: 60, label: unit }]}
        margin={{ left: 70, right: 30, top: 20, bottom: 50 }}
      />
    </Box>
  );
}

export function ElectricityCostWidget({
  osmName, dateFromValue, dateToValue, chartType, colour,
}: WidgetComponentProps) {
  const [data, setData]       = React.useState<CostDataPoint[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError]     = React.useState<string | null>(null);
  const fillColor = colour ?? '#f59e0b';

  React.useEffect(() => {
    if (!dateFromValue || !dateToValue) return;
    setLoading(true);
    getElectricityCost(osmName, dateFromValue.toISOString(), dateToValue.toISOString())
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [osmName, dateFromValue, dateToValue]);

  if (loading) return <CircularProgress />;
  if (error)   return <Typography color="error">{error}</Typography>;

  const points: SeriesPoint[] = data.map((d) => ({ bucket: d.bucket, value: d.cost_gbp }));
  return renderChart(chartType, points, 'Electricity Cost', '£', fillColor);
}

type PulseWidgetProps = WidgetComponentProps & {
  label:      string;
  defaultUnit: string;
  fillColor:  string;
};

function PulseConsumptionWidget({
  osmName, dateFromValue, dateToValue, chartType, config, colour,
  label, defaultUnit, fillColor: defaultColor,
}: PulseWidgetProps) {
  const [data, setData]       = React.useState<ConsumptionDataPoint[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError]     = React.useState<string | null>(null);

  const cfg       = config as PulseConsumptionConfig | null;
  const channel   = cfg?.channel   ?? 'CNT1';
  const pulseDiv = cfg?.pulseDiv ?? 1.0;
  const unit      = cfg?.unit      ?? defaultUnit;
  const fillColor = colour ?? defaultColor;

  React.useEffect(() => {
    if (!dateFromValue || !dateToValue) return;
    setLoading(true);
    getPulseConsumption(
      osmName,
      dateFromValue.toISOString(),
      dateToValue.toISOString(),
      channel,
      pulseDiv,
    )
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [osmName, dateFromValue, dateToValue, channel, pulseDiv]);

  if (loading) return <CircularProgress />;
  if (error)   return <Typography color="error">{error}</Typography>;

  const points: SeriesPoint[] = data.map((d) => ({ bucket: d.bucket, value: d.consumption }));
  return renderChart(chartType, points, label, unit, fillColor);
}

export function GasConsumptionWidget(props: WidgetComponentProps) {
  return (
    <PulseConsumptionWidget
      {...props}
      label="Gas Consumption"
      defaultUnit="m³"
      fillColor="#3b82f6"
    />
  );
}

export function WaterConsumptionWidget(props: WidgetComponentProps) {
  return (
    <PulseConsumptionWidget
      {...props}
      label="Water Consumption"
      defaultUnit="L"
      fillColor="#06b6d4"
    />
  );
}

export type CostConfig = {
  channel:  'CNT1' | 'CNT2';
  pulseDiv: number;
};

type PulseCostWidgetProps = WidgetComponentProps & {
  label:      string;
  defaultColor: string;
  fetchFn: (osmName: string, from: string, to: string, channel: 'CNT1' | 'CNT2', pulseDiv: number) => Promise<CostDataPoint[]>;
};

function PulseCostWidget({
  osmName, dateFromValue, dateToValue, chartType, config, colour,
  label, defaultColor, fetchFn,
}: PulseCostWidgetProps) {
  const [data, setData]       = React.useState<CostDataPoint[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError]     = React.useState<string | null>(null);

  const cfg      = config as CostConfig | null;
  const channel  = cfg?.channel  ?? 'CNT1';
  const pulseDiv = cfg?.pulseDiv ?? 100;
  const fillColor = colour ?? defaultColor;

  React.useEffect(() => {
    if (!dateFromValue || !dateToValue) return;
    setLoading(true);
    fetchFn(osmName, dateFromValue.toISOString(), dateToValue.toISOString(), channel, pulseDiv)
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [osmName, dateFromValue, dateToValue, channel, pulseDiv]);

  if (loading) return <CircularProgress />;
  if (error)   return <Typography color="error">{error}</Typography>;

  const points: SeriesPoint[] = data.map((d) => ({ bucket: d.bucket, value: d.cost_gbp }));
  return renderChart(chartType, points, label, '£', fillColor);
}

export function GasCostWidget(props: WidgetComponentProps) {
  return (
    <PulseCostWidget
      {...props}
      label="Gas Cost"
      defaultColor="#f97316"
      fetchFn={getGasCost}
    />
  );
}

export function WaterCostWidget(props: WidgetComponentProps) {
  return (
    <PulseCostWidget
      {...props}
      label="Water Cost"
      defaultColor="#06b6d4"
      fetchFn={getWaterCost}
    />
  );
}

export function VibrationWidget({
  osmName, dateFromValue, dateToValue, chartType, colour,
}: WidgetComponentProps) {
  const [data, setData]       = React.useState<VibrationDataPoint[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError]     = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!dateFromValue || !dateToValue) return;
    setLoading(true);
    getVibration(osmName, dateFromValue.toISOString(), dateToValue.toISOString())
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [osmName, dateFromValue, dateToValue]);

  if (loading) return <CircularProgress />;
  if (error)   return <Typography color="error">{error}</Typography>;
  if (!data.length) return <Typography>No data found</Typography>;

  // ISO 10816 thresholds (mm/s RMS)
  const ISO_LINES = [
    { value: 2.8,  label: 'Good',             color: '#1D9E75' },
    { value: 4.5,  label: 'Unsatisfactory',   color: '#BA7517' },
    { value: 7.1,  label: 'Dangerous',        color: '#D4537E' },
  ];

  const xLabels = data.map((d) =>
    new Date(d.bucket).toLocaleString('en-GB', {
      day: '2-digit', month: 'short',
      hour: '2-digit', minute: '2-digit',
    })
  );

  const allValues = data.flatMap(d => [d.zrv1, d.xrv1, d.zpv1, d.xpv1]);
  const yMax      = Math.max(Math.max(...allValues) * 1.1, 3);

  const series = [
    { data: data.map((d) => d.zrv1), label: 'Z-Axis RMS',  showMark: false, valueFormatter: (v: number | null) => v !== null ? `${v.toFixed(4)} mm/s` : '' },
    { data: data.map((d) => d.xrv1), label: 'X-Axis RMS',  showMark: false, valueFormatter: (v: number | null) => v !== null ? `${v.toFixed(4)} mm/s` : '' },
    { data: data.map((d) => d.zpv1), label: 'Z-Axis Peak', showMark: false, valueFormatter: (v: number | null) => v !== null ? `${v.toFixed(4)} mm/s` : '' },
    { data: data.map((d) => d.xpv1), label: 'X-Axis Peak', showMark: false, valueFormatter: (v: number | null) => v !== null ? `${v.toFixed(4)} mm/s` : '' },
  ];

  if (chartType === 'bar') {
    return (
      <Box sx={{ width: '100%', height: 300 }}>
        <BarChart
          colors={['#1D9E75', '#378ADD', '#BA7517', '#D4537E']}
          series={series}
          xAxis={[{ data: xLabels }]}
          yAxis={[{ width: 60, label: 'mm/s' }]}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height: 300, position: 'relative' }}>
      <LineChart
        colors={['#1D9E75', '#378ADD', '#BA7517', '#D4537E']}
        series={series}
        xAxis={[{ scaleType: 'point', data: xLabels }]}
        yAxis={[{ width: 50, label: 'mm/s', min: 0, max: yMax }]}
        margin={{ left: 60, right: 30, top: 20, bottom: 50 }}
        sx={{ '& .MuiMarkElement-root': { display: 'none' } }}
      />

      {/* ISO 10816 legend */}
      <Box sx={{
        position:      'absolute',
        top:           24,
        right:         36,
        display:       'flex',
        flexDirection: 'column',
        gap:           '2px',
        background:    'rgba(0,0,0,0.5)',
        borderRadius:  1,
        p:             '4px 8px',
      }}>
        <Typography sx={{
          fontFamily:    "'DM Mono', monospace",
          fontSize:      9,
          color:         'rgba(255,255,255,0.5)',
          letterSpacing: '0.05em',
          mb:            '2px',
        }}>
          ISO 10816 Limits
        </Typography>
        {ISO_LINES.map(l => (
          <Box key={l.label} sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Box sx={{ width: 16, height: 2, background: l.color, borderRadius: 1 }} />
            <Typography sx={{
              fontFamily: "'DM Mono', monospace",
              fontSize:   9,
              color:      'rgba(255,255,255,0.7)',
            }}>
              {l.label} ({l.value})
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}


export function ImportCostWidget({
  osmName, dateFromValue, dateToValue, chartType, config, colour,
}: WidgetComponentProps) {
  const [data, setData]       = React.useState<CostDataPoint[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError]     = React.useState<string | null>(null);

  const cfg       = config as { tag?: string } | null;
  const tag       = cfg?.tag ?? 'elec';
  const fillColor = colour ?? '#a855f7';

  React.useEffect(() => {
    if (!dateFromValue || !dateToValue) return;
    setLoading(true);
    getImportCost(osmName, dateFromValue.toISOString(), dateToValue.toISOString(), tag)
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [osmName, dateFromValue, dateToValue, tag]);

  if (loading) return <CircularProgress />;
  if (error)   return <Typography color="error">{error}</Typography>;

  const points: SeriesPoint[] = data.map((d) => ({ bucket: d.bucket, value: d.cost_gbp }));
  return renderChart(chartType, points, 'Imported Power Cost', '£', fillColor);
}

export function ImportConsumptionWidget({
  osmName, dateFromValue, dateToValue, chartType, colour,
}: WidgetComponentProps) {
  const [data, setData]       = React.useState<ConsumptionDataPoint[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError]     = React.useState<string | null>(null);

  const fillColor = colour ?? '#6366f1';

  React.useEffect(() => {
    if (!dateFromValue || !dateToValue) return;
    setLoading(true);
    getImportConsumption(osmName, dateFromValue.toISOString(), dateToValue.toISOString())
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [osmName, dateFromValue, dateToValue]);

  if (loading) return <CircularProgress />;
  if (error)   return <Typography color="error">{error}</Typography>;

  const points: SeriesPoint[] = data.map((d) => ({ bucket: d.bucket, value: d.consumption }));
  return renderChart(chartType, points, 'Power Consumption', 'kWh', fillColor);
}


export function AirQualityWidget({
  osmName, dateFromValue, dateToValue, chartType,
}: WidgetComponentProps) {
  const [data, setData]       = React.useState<AirQualityDataPoint[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError]     = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!dateFromValue || !dateToValue) return;
    setLoading(true);
    getAirQuality(osmName, dateFromValue.toISOString(), dateToValue.toISOString())
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [osmName, dateFromValue, dateToValue]);

  if (loading) return <CircularProgress />;
  if (error)   return <Typography color="error">{error}</Typography>;
  if (!data.length) return <Typography>No data found</Typography>;

  const DAQI_LINES = [
    { value: 11,  label: 'Low',       color: '#1D9E75' },
    { value: 35,  label: 'Moderate',  color: '#BA7517' },
    { value: 53,  label: 'High',      color: '#D4537E' },
    { value: 70,  label: 'Very High', color: '#993C1D' },
  ];

  const xLabels = data.map((d) => new Date(d.bucket).toLocaleString());
  const allValues = data.flatMap(d => [d.pm1, d.pm4, d.pm10, d.pm25]);
  const yMax = Math.max(Math.max(...allValues) * 1.1, 75);

  if (chartType === 'bar') {
    return (
      <Box sx={{ width: '100%', height: 300 }}>
        <BarChart
          series={[
            { data: data.map((d) => d.pm1),  label: 'PM1',   color: '#378ADD', showMark: false, valueFormatter: (v: number | null) => v !== null ? `${v.toFixed(2)} μg/m³` : '' },
            { data: data.map((d) => d.pm4),  label: 'PM4',   color: '#BA7517', showMark: false, valueFormatter: (v: number | null) => v !== null ? `${v.toFixed(2)} μg/m³` : '' },
            { data: data.map((d) => d.pm10), label: 'PM10',  color: '#D4537E', showMark: false, valueFormatter: (v: number | null) => v !== null ? `${v.toFixed(2)} μg/m³` : '' },
            { data: data.map((d) => d.pm25), label: 'PM2.5', color: '#993C1D', showMark: false, valueFormatter: (v: number | null) => v !== null ? `${v.toFixed(2)} μg/m³` : '' },
          ]}
          xAxis={[{ data: xLabels, label: 'Time' }]}
          yAxis={[{ width: 60, label: 'μg/m³' }]}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height: 300 }}>
      <LineChart
        colors={['#378ADD', '#BA7517', '#D4537E', '#993C1D']}
        series={[
          { data: data.map((d) => d.pm1),  label: 'PM1',   showMark: false, valueFormatter: (v: number | null) => v !== null ? `${v.toFixed(2)} μg/m³` : '' },
          { data: data.map((d) => d.pm4),  label: 'PM4',   showMark: false, valueFormatter: (v: number | null) => v !== null ? `${v.toFixed(2)} μg/m³` : '' },
          { data: data.map((d) => d.pm10), label: 'PM10',  showMark: false, valueFormatter: (v: number | null) => v !== null ? `${v.toFixed(2)} μg/m³` : '' },
          { data: data.map((d) => d.pm25), label: 'PM2.5', showMark: false, valueFormatter: (v: number | null) => v !== null ? `${v.toFixed(2)} μg/m³` : '' },
        ]}
        xAxis={[{ scaleType: 'point', data: xLabels }]}
        yAxis={[{ width: 50, label: 'μg/m³', min: 0, max: yMax }]}
        margin={{ left: 60, right: 30, top: 20, bottom: 50 }}
        sx={{ '& .MuiMarkElement-root': { display: 'none' } }}
      />

      {/* DAQI threshold legend */}
      <Box sx={{
        position:      'absolute',
        top:           24,
        right:         36,
        display:       'flex',
        flexDirection: 'column',
        gap:           '2px',
        background:    'rgba(0,0,0,0.5)',
        borderRadius:  1,
        p:             '4px 8px',
      }}>
        <Typography sx={{
          fontFamily:    "'DM Mono', monospace",
          fontSize:      9,
          color:         'rgba(255,255,255,0.5)',
          letterSpacing: '0.05em',
          mb:            '2px',
        }}>
          DAQI
        </Typography>
        {DAQI_LINES.map(l => (
          <Box key={l.label} sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Box sx={{ width: 16, height: 2, background: l.color, borderRadius: 1 }} />
            <Typography sx={{
              fontFamily: "'DM Mono', monospace",
              fontSize:   9,
              color:      'rgba(255,255,255,0.7)',
            }}>
              {l.label} ({l.value})
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export function ThreePhaseCurrentWidget({
  osmName, dateFromValue, dateToValue, chartType,
}: WidgetComponentProps) {
  const [data, setData]       = React.useState<ThreePhaseDataPoint[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError]     = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!dateFromValue || !dateToValue) return;
    setLoading(true);
    getThreePhaseCurrent(osmName, dateFromValue.toISOString(), dateToValue.toISOString())
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [osmName, dateFromValue, dateToValue]);

  if (loading) return <CircularProgress />;
  if (error)   return <Typography color="error">{error}</Typography>;
  if (!data.length) return <Typography>No data found</Typography>;

  const xLabels = data.map((d) => new Date(d.bucket).toLocaleString());

  const series = [
    { data: data.map((d) => d.cc1), label: 'Phase 1 (CC1)', showMark: false,
      valueFormatter: (v: number | null) => v !== null ? `${v.toFixed(2)} mA` : '' },
    { data: data.map((d) => d.cc2), label: 'Phase 2 (CC2)', showMark: false,
      valueFormatter: (v: number | null) => v !== null ? `${v.toFixed(2)} mA` : '' },
    { data: data.map((d) => d.cc3), label: 'Phase 3 (CC3)', showMark: false,
      valueFormatter: (v: number | null) => v !== null ? `${v.toFixed(2)} mA` : '' },
  ];

  if (chartType === 'bar') {
    return (
      <Box sx={{ width: '100%', height: 300 }}>
        <BarChart
          series={series}
          xAxis={[{ data: xLabels, label: 'Time' }]}
          yAxis={[{ width: 60, label: 'mA' }]}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height: 300 }}>
      <LineChart
        series={series}
        xAxis={[{ scaleType: 'point', data: xLabels, label: 'Time' }]}
        yAxis={[{ width: 60, label: 'mA' }]}
        margin={{ left: 70, right: 30, top: 20, bottom: 50 }}
      />
    </Box>
  );
}

export function GaugeWidget({
  osmName, dateFromValue, dateToValue, chartType, config, colour, min, max, measurement,
}: WidgetComponentProps) {
  const cfg       = config as SensorGaugeConfig | null;
  const meas      = cfg?.measurement ?? measurement ?? '';
  const minVal    = cfg?.min  ?? min  ?? 0;
  const maxVal    = cfg?.max  ?? max  ?? 100;
  const fillColor = cfg?.colour ?? colour ?? '#1976d2';
  const measurements = React.useMemo(() => [meas], [meas]);

  const data = BaseChartVisualization({
    table: 'pg_sf_ds', valuename: 'ds_val', timeField: 'bucket',
    measurements, timeFrom: dateFromValue, timeTo: dateToValue,
    unit: '', osmName,
  });

  if (!data || !data.series?.length) return <Typography>No data found</Typography>;

  const values = data.series[0].data.map(Number).filter((n: number) => !isNaN(n));
  const avg    = values.reduce((s: number, v: number) => s + v, 0) / values.length;

  if (chartType === 'stat') {
    return (
      <Box sx={{ textAlign: 'center', py: 2 }}>
        <Typography variant="h3" fontWeight="bold" color={fillColor}>
          {avg.toFixed(2)}
        </Typography>
        <Typography variant="body2" color="text.secondary">{DESC_MAP[meas] ?? meas}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Gauge
        value={avg}
        valueMin={minVal}
        valueMax={maxVal}
        cornerRadius="50%"
        sx={{
          [`& .${gaugeClasses.valueText}`]: { fontSize: 20, fontWeight: 'bold' },
          [`& .${gaugeClasses.valueArc}`]:  { fill: fillColor },
        }}
      />
      <Typography variant="body2" color="text.secondary">{DESC_MAP[meas] ?? meas}</Typography>
    </Box>
  );
}

export function ChartWidget({
  osmName, dateFromValue, dateToValue, chartType, config, colour,
}: WidgetComponentProps) {
  const cfg       = config as SensorGaugeConfig | null;
  const meas      = cfg?.measurement ?? '';
  const fillColor = colour ?? '#1976d2';
  const label     = DESC_MAP[meas] ?? meas;
  const measurements = React.useMemo(() => [meas], [meas]);

  const data = BaseChartVisualization({
    table: 'pg_sf_ds', valuename: 'ds_val', timeField: 'bucket',
    measurements, timeFrom: dateFromValue, timeTo: dateToValue,
    unit: '', osmName,
  });

  if (!data || !data.series?.length) return <Typography>No data found</Typography>;

  const series = data.series.map((s: any) => ({
    ...s,
    color:    fillColor,
    showMark: false,
  }));

  if (chartType === 'bar') {
    return (
      <Box sx={{ width: '100%', height: 300 }}>
        <BarChart
          series={series}
          xAxis={[{ data: data.xLabels, label: 'Time' }]}
          yAxis={[{ width: 60, label: label }]}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height: 300 }}>
      <LineChart
        series={series}
        xAxis={[{ scaleType: 'point', data: data.xLabels, label: 'Time' }]}
        yAxis={[{ width: 60, label: label }]}
        margin={{ left: 70, right: 30, top: 20, bottom: 50 }}
      />
    </Box>
  );
}

export const WIDGET_COMPONENT_MAP: Record<string, React.ComponentType<WidgetComponentProps>> = {
  electricity_cost:    ElectricityCostWidget,
  gas_consumption:     GasConsumptionWidget,
  water_consumption:   WaterConsumptionWidget,
  gas_cost:            GasCostWidget,
  water_cost:          WaterCostWidget,
  vibration:           VibrationWidget,
  import_cost:         ImportCostWidget,
  import_consumption:  ImportConsumptionWidget,
  air_quality:         AirQualityWidget,
  three_phase_current: ThreePhaseCurrentWidget,
  sensor_gauge:        GaugeWidget,
  sensor_chart:        ChartWidget,
};
